import {getTestProps} from '@benchling/js-test-utils';
import {Checkbox, createStyles, Stack, Text, UnstyledButton} from '@mantine/core';
import {useId} from '@mantine/hooks';
import React from 'react';

import {assert} from 'Asserts';
import {CustomComponentEditorProps} from 'pipelines/generalized-step-config-editor/StepConfigEditorTypes';
import {EnumOptions, GetPipelineConfigSpecType} from 'pipelines/StepShapeUtils';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

import {isEnumOptionsArray} from '../EnumEditor';

/**
 * A scratch plate stores its data as several independent "layers" over the same well grid (see
 * `benchling/plates/scratch_plates/layers/`). This copy explains, per layer, what data it holds so a
 * user picking layers to extract understands what each output dataset will contain. Keyed by the enum
 * value in `ScratchPlateLayerName` — keep in sync with that enum.
 */
const LAYER_DETAILS: Record<string, {description: string; iconName: BenchlingIconName}> = {
  content: {
    description: 'The samples and reagents placed in each well, including amounts and concentrations.',
    iconName: 'flask-vial',
  },
  label: {
    description: 'Categorical labels laid out across the wells, such as sample roles or conditions.',
    iconName: 'tag',
  },
  concentration_design: {
    description: 'Named concentration gradients defined across the plate.',
    iconName: 'vials',
  },
  metadata: {
    description: 'Custom typed fields to annotate the wells.',
    iconName: 'table',
  },
  parameter: {
    description: 'Procedure data-parameter values recorded for each well.',
    iconName: 'sliders',
  },
  measurement: {
    description: 'Instrument readings captured for each well.',
    iconName: 'gauge',
  },
};

const useStyles = createStyles((theme, {selected, disabled}: {selected: boolean; disabled: boolean}) => ({
  card: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing.sm,
    padding: theme.spacing.sm,
    borderRadius: theme.radius.sm,
    border: `1px solid ${selected ? theme.colors.blue[5] : theme.colors.gray[3]}`,
    backgroundColor: selected ? theme.colors.blue[0] : theme.white,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.6 : 1,
    transition: 'border-color 100ms ease, background-color 100ms ease',
    '&:hover': disabled
      ? undefined
      : {
          borderColor: selected ? theme.colors.blue[6] : theme.colors.gray[5],
        },
  },
  icon: {
    color: selected ? theme.colors.blue[6] : theme.colors.gray[6],
    marginTop: 2,
  },
}));

interface LayerOption {
  value: string;
  label: string;
}

interface LayerCardProps {
  option: LayerOption;
  selected: boolean;
  disabled: boolean;
  onToggle: (value: string) => void;
}

function LayerCard({option, selected, disabled, onToggle}: LayerCardProps): JSX.Element {
  const {classes} = useStyles({selected, disabled});
  const details = LAYER_DETAILS[option.value];
  const descriptionId = useId();
  return (
    <UnstyledButton
      className={classes.card}
      disabled={disabled}
      onClick={() => {
        onToggle(option.value);
      }}
      role="checkbox"
      aria-checked={selected}
      aria-label={option.label}
      aria-describedby={details != null ? descriptionId : undefined}
    >
      <Checkbox checked={selected} disabled={disabled} readOnly={true} tabIndex={-1} aria-hidden={true} />
      {details != null && (
        <span className={classes.icon}>
          <BenchlingIcon icon={details.iconName} />
        </span>
      )}
      <Stack spacing={2}>
        <Text size="md" weight={500}>
          {option.label}
        </Text>
        {details != null && (
          <Text id={descriptionId} size="sm" color="dimmed">
            {details.description}
          </Text>
        )}
      </Stack>
    </UnstyledButton>
  );
}

const LAYERS_FIELD_NAME = 'layers';

/**
 * Config editor for PLATES_TO_DATASETS. Renders an intro explaining the layer model, then one
 * selectable card per plate layer showing its name, icon, and a short description of the data it holds.
 * Each selected layer becomes its own output dataset.
 */
export default function LayerSelector({
  config,
  configShape,
  onChangeConfig,
  disabled = false,
}: CustomComponentEditorProps): JSX.Element {
  assert(configShape.kind === 'shape', 'LayerSelector requires a shape config');
  const layersShape = configShape.fields[LAYERS_FIELD_NAME];
  assert(layersShape?.kind === 'array', 'LayerSelector requires a layers array field');
  const {choices} = layersShape.elementSpec as GetPipelineConfigSpecType<'enum'>;

  const options: Array<LayerOption> = isEnumOptionsArray(choices)
    ? choices.map((choice: EnumOptions) => ({
        value: String(choice.value),
        label: choice.displayName ?? String(choice.value),
      }))
    : choices.map((choice: string | number) => ({value: String(choice), label: String(choice)}));

  const selectedLayers = (config[LAYERS_FIELD_NAME] as Array<string> | null) ?? [];

  const toggleLayer = (value: string): void => {
    const nextLayers = selectedLayers.includes(value)
      ? selectedLayers.filter((layer) => layer !== value)
      : [...selectedLayers, value];
    onChangeConfig(config, [LAYERS_FIELD_NAME], nextLayers);
  };

  return (
    <Stack spacing="sm" {...getTestProps({component: 'StepConfigEditor', element: 'layerSelector'})}>
      <Stack spacing={2}>
        <Text size="md" weight={600}>
          Select datasets to extract
        </Text>
        <Text size="sm" color="dimmed">
          A plate keeps several kinds of data as separate layers over the same wells. Each layer you select
          becomes its own dataset.
        </Text>
      </Stack>
      <Stack spacing="xs">
        {options.map((option) => (
          <LayerCard
            key={option.value}
            option={option}
            selected={selectedLayers.includes(option.value)}
            disabled={disabled}
            onToggle={toggleLayer}
          />
        ))}
      </Stack>
    </Stack>
  );
}
